import { useEffect, useState } from "react";
import { Sparkles, X, Loader2, Send, Copy, Check } from "lucide-react";
import { api, apiError } from "@/lib/api";

/**
 * AIAssistant — small topbar trigger + modal. The modal contents are the same
 * as before; the old floating FAB was intrusive on smaller screens so the
 * trigger now lives in the topbar (see `AIAssistant.Trigger`).
 *
 * Usage in AppLayout:
 *   <AIAssistant.Trigger />   ← place inside the topbar
 *   <AIAssistant />            ← place once, at the end of the shell
 *
 * Or use the default export standalone (it renders its own trigger via
 * a global event listener that Trigger emits).
 */
const OPEN_EVENT = "pit-link:open-ai";

export default function AIAssistant() {
  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState(false);

  // Listen for the topbar trigger's custom event so we don't need to share
  // React state between them.
  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener(OPEN_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_EVENT, onOpen);
  }, []);

  const ask = async () => {
    if (!prompt.trim() || busy) return;
    setBusy(true); setErr(""); setResult(null);
    try {
      const { data } = await api.post("/ai-assistant/interpret", {
        prompt,
        text: prompt,
        query: prompt,
      });
      setResult(data);
    } catch (ex) {
      setErr(apiError(ex));
    } finally {
      setBusy(false);
    }
  };

  const copy = () => {
    const text = typeof result === "string" ? result : JSON.stringify(result, null, 2);
    navigator.clipboard?.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  const summary =
    (result && (result.summary || result.answer || result.response || result.text)) || null;

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/70 flex items-end md:items-center md:justify-center p-4"
      onClick={() => setOpen(false)}
      data-testid="ai-modal"
    >
      <div
        className="w-full md:max-w-2xl max-h-[85vh] panel-raised overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5" style={{ borderBottom: "1px solid var(--border)" }}>
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: "rgba(245,130,32,0.15)", color: "var(--primary)", border: "1px solid rgba(245,130,32,0.3)" }}
            >
              <Sparkles className="w-5 h-5" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted">AI Assistant</div>
              <div className="font-display font-black text-lg tracking-tight">Ask anything</div>
            </div>
          </div>
          <button className="btn btn-sm btn-ghost" onClick={() => setOpen(false)}>
            <X className="w-4 h-4" /> Close
          </button>
        </div>

        <div className="p-5 overflow-auto space-y-4">
          <div className="field">
            <label>Prompt</label>
            <textarea
              className="textarea" rows={3} autoFocus
              placeholder="e.g. Summarise all breakdowns from Site A this week, or ‘log a hazard: hydraulic hose leaking on CNC-12’"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={(e) => {
                if ((e.metaKey || e.ctrlKey) && e.key === "Enter") ask();
              }}
              data-testid="ai-prompt"
            />
            <div className="text-[10px] uppercase tracking-widest text-muted">
              Cmd/Ctrl + Enter to send · uses /api/ai-assistant/interpret
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            {[
              "Summarise today's shift handovers",
              "Which assets have the most breakdowns this week?",
              "Log a hazard for hydraulic leak on CNC-12",
              "Draft a Planning Request for weekly conveyor lubrication",
            ].map((s) => (
              <button
                key={s}
                type="button"
                className="pill pill-orange hover:brightness-110"
                onClick={() => setPrompt(s)}
              >
                {s}
              </button>
            ))}
          </div>

          {err && (
            <div className="pill pill-red !text-xs !py-2 !px-3 justify-start">{err}</div>
          )}

          {result && (
            <div className="panel-flat p-4 space-y-3" data-testid="ai-result">
              <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-widest text-muted">Answer</div>
                <button className="text-[11px] uppercase tracking-widest text-muted hover:text-white flex items-center gap-1" onClick={copy}>
                  {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  {copied ? "Copied" : "Copy"}
                </button>
              </div>
              {summary && (
                <div className="text-sm leading-relaxed whitespace-pre-wrap">{summary}</div>
              )}
              {typeof result === "object" && !summary && (
                <pre className="text-xs font-mono-tight whitespace-pre-wrap break-all p-3 rounded-lg max-h-72 overflow-auto"
                     style={{ background: "var(--bg)", border: "1px solid var(--border)" }}>
{JSON.stringify(result, null, 2)}
                </pre>
              )}
            </div>
          )}
        </div>

        <div className="p-5 flex justify-end gap-2" style={{ borderTop: "1px solid var(--border)" }}>
          <button className="btn btn-ghost" onClick={() => { setPrompt(""); setResult(null); setErr(""); }}>Reset</button>
          <button
            className="btn btn-primary"
            onClick={ask}
            disabled={busy || !prompt.trim()}
            data-testid="ai-send"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4" /> Ask</>}
          </button>
        </div>
      </div>
    </div>
  );
}

AIAssistant.Trigger = function AIAssistantTrigger() {
  const open = () => window.dispatchEvent(new CustomEvent(OPEN_EVENT));
  return (
    <button
      onClick={open}
      className="btn btn-sm btn-ghost"
      title="Ask Pit Link AI"
      data-testid="ai-trigger"
    >
      <Sparkles className="w-4 h-4" style={{ color: "var(--primary)" }} />
      <span className="hidden md:inline">Ask AI</span>
    </button>
  );
};

